import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useDroppable } from '@dnd-kit/core';
import TaskCard from './TaskCard';

// null serverTimestamp (local write awaiting ack) sorts as newest
const sortTimestamp = (ts) => ts?.toMillis?.() ?? Number.MAX_SAFE_INTEGER;

/**
 * Board column (Tasks PRD §4, §7). Incomplete tasks render newest-first;
 * completed tasks render below a centered "Completed" divider, most recently
 * completed first. The divider only appears when both sections are visible
 * (status filter = All).
 */
const TaskColumn = ({ column, tasks, statusFilter, usersByUid, clientsById, onOpenTask, onAddTask }) => {
  const { setNodeRef, isOver } = useDroppable({ id: column.key });

  const incomplete = tasks
    .filter((t) => !t.completed)
    .sort((a, b) => sortTimestamp(b.createdAt) - sortTimestamp(a.createdAt));
  const completed = tasks
    .filter((t) => t.completed)
    .sort((a, b) => sortTimestamp(b.completedAt) - sortTimestamp(a.completedAt));

  const renderCard = (task) => (
    <TaskCard
      key={task.id}
      task={task}
      assignee={task.assigneeUid ? usersByUid[task.assigneeUid] : null}
      clientName={task.clientId ? clientsById[task.clientId]?.clientName : null}
      onClick={() => onOpenTask(task)}
    />
  );

  return (
    <Box
      ref={setNodeRef}
      sx={{
        bgcolor: isOver ? '#e8eef5' : '#f4f5f7',
        borderRadius: 1.5,
        border: isOver ? '2px dashed #4C9AFF' : '2px solid transparent',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 200,
        minWidth: 0,
        transition: 'background 150ms, border-color 150ms'
      }}
    >
      <Box sx={{ px: 1.75, pt: 1.75, pb: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography component="span" sx={{ fontSize: '1rem', lineHeight: 1 }}>
          {column.emoji}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em'
          }}
        >
          {column.label}
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary', ml: 0.25 }}>
          {tasks.length}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 60,
          px: 1.25,
          pb: 1.25,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          overflowY: 'auto'
        }}
      >
        {statusFilter !== 'completed' && incomplete.map(renderCard)}
        {statusFilter === 'all' && completed.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pt: 1.5,
              pb: 0.5,
              px: 0.5,
              fontSize: '0.6875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'text.secondary',
              '&::before, &::after': {
                content: '""',
                flex: 1,
                height: '1px',
                bgcolor: 'divider'
              }
            }}
          >
            Completed
          </Box>
        )}
        {statusFilter !== 'incomplete' && completed.map(renderCard)}
      </Box>

      <Button
        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
        onClick={() => onAddTask(column.key)}
        sx={{
          m: 0.5,
          mx: 1,
          justifyContent: 'flex-start',
          color: 'text.secondary',
          fontSize: '0.8125rem',
          textTransform: 'none',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.06)', color: 'text.primary' }
        }}
      >
        Add task
      </Button>
    </Box>
  );
};

export default TaskColumn;
